import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { onWorkspaceChange } from "@/lib/active-workspace";
import {
  ArrowRight,
  Sparkles,
  Check,
  Calendar,
  Building2,
  Target,
  TrendingUp,
  TrendingDown,
  Clock,
  Edit3,
  FileText,
  MessageSquare,
  Video,
  Mic,
  ChevronRight,
  Lock,
  Unlock,
  Trophy,
  Hourglass,
  CheckCircle2,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import GuestGate from "@/components/GuestGate";
import { useNavigate } from "react-router-dom";
import useBillingPlan from "@/lib/use-billing-plan";
import UpgradeLiveModal from "@/components/UpgradeLiveModal";
import { useEntitlement } from "@/lib/entitlement-context";
import ScoreInfoTip from "@/components/ScoreInfoTip";
import MilestoneFeed from "@/components/MilestoneFeed";

function AnimatedNumber({ value = 0, duration = 900, className = "" }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    let start = 0;
    const t0 = performance.now();
    const target = Number(value) || 0;
    let raf;
    const step = (now) => {
      const p = Math.min(1, (now - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(start + (target - start) * eased));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return <span className={className}>{display}</span>;
}

function bandColor(v) {
  if (v >= 80) return { ring: "#10B981", text: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/25" };
  if (v >= 55) return { ring: "#F59E0B", text: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/25" };
  return { ring: "#8B5CF6", text: "text-violet-300", bg: "bg-violet-500/10", border: "border-violet-500/25" };
}

function SectionHeader({ label, subtitle, testid, tone = "amber" }) {
  return (
    <div className="mb-4" data-testid={testid}>
      <div className={`text-[11px] uppercase tracking-[0.28em] mb-1.5 font-medium ${tone === "neutral" ? "text-zinc-200" : "text-amber-400"}`}>
        {label}
      </div>
      <div className="text-sm text-zinc-400 leading-relaxed">{subtitle}</div>
    </div>
  );
}

/**
 * formatInterviewDateLabel - human-friendly label for the Interview Snapshot.
 * Returns "Today, 3:00 PM", "Tomorrow, 1:30 PM", "Monday, 10:00 AM", or a
 * short calendar date for anything further out. Returns null when no date.
 */
function formatInterviewDateLabel(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfTarget = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dayDiff = Math.round((startOfTarget - startOfToday) / (86400 * 1000));
  const timeStr = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (dayDiff === 0) return `Today, ${timeStr}`;
  if (dayDiff === 1) return `Tomorrow, ${timeStr}`;
  if (dayDiff > 0 && dayDiff <= 6) {
    return `${d.toLocaleDateString([], { weekday: "long" })}, ${timeStr}`;
  }
  return `${d.toLocaleDateString([], { month: "short", day: "numeric" })}, ${timeStr}`;
}

/**
 * CountdownChip - a live "T-minus" chip that keeps the user's interview day
 * top-of-mind. Color escalates as the date gets closer:
 *   > 7 days   → cool zinc     ("In 12 days")
 *   3–7 days   → amber         ("In 4 days")
 *   1–2 days   → bold amber    ("Tomorrow" / "In 2 days")
 *   <24h >1h   → rose          ("In 6 hours")
 *   <1h        → pulsing rose  ("In 45 min" / "Any moment")
 *   past       → zinc          ("Interview done · how did it go?")
 * Returns null when no date. Re-renders once a minute for hour-accurate copy.
 */
function CountdownChip({ interviewDate }) {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!interviewDate) return;
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, [interviewDate]);
  if (!interviewDate) return null;
  const target = new Date(interviewDate).getTime();
  if (isNaN(target)) return null;

  const diffMs = target - now;
  const absMs = Math.abs(diffMs);
  const mins = Math.floor(absMs / 60_000);
  const hours = Math.floor(absMs / 3_600_000);
  const days = Math.floor(absMs / (24 * 3_600_000));

  // Bucketed copy + tone
  let label, tone, urgent = false;
  if (diffMs < 0) {
    label = "Interview done · how did it go?";
    tone = "past";
  } else if (mins < 1) {
    label = "Any moment now";
    tone = "critical"; urgent = true;
  } else if (mins < 60) {
    label = `In ${mins} min`;
    tone = "critical"; urgent = true;
  } else if (hours < 24) {
    label = hours === 1 ? "In 1 hour" : `In ${hours} hours`;
    tone = "hot";
  } else if (days === 1) {
    label = "Tomorrow";
    tone = "warm";
  } else if (days <= 2) {
    label = `In ${days} days`;
    tone = "warm";
  } else if (days <= 7) {
    label = `In ${days} days`;
    tone = "notice";
  } else {
    label = `In ${days} days`;
    tone = "cool";
  }

  const toneClasses = {
    critical: "bg-rose-500/[0.12] border-rose-500/40 text-rose-200",
    hot: "bg-rose-500/[0.08] border-rose-500/30 text-rose-200",
    warm: "bg-amber-500/[0.12] border-amber-500/40 text-amber-200",
    notice: "bg-amber-500/[0.06] border-amber-500/25 text-amber-300",
    cool: "bg-zinc-800/60 border-zinc-700/70 text-zinc-300",
    past: "bg-zinc-900/60 border-zinc-800 text-zinc-400",
  }[tone];

  return (
    <span
      data-testid="interview-countdown-chip"
      data-tone={tone}
      title={new Date(interviewDate).toLocaleString([], { dateStyle: "full", timeStyle: "short" })}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold tabular-nums transition-colors ${toneClasses}`}
    >
      <Hourglass className={`h-3 w-3 ${urgent ? "animate-pulse" : ""}`} />
      {label}
    </span>
  );
}

/**
 * missionActionDescription - pure "what" copy for the Today's Mission card.
 * Strips the coaching/analysis language the backend sometimes returns so the
 * card describes only the next action (coaching lives in Today's Coaching).
 */
function missionActionDescription(mission) {
  if (!mission) return "";
  const mins = mission.estimated_minutes || 15;
  const title = mission.title || "";
  const m = title.match(/^(?:Sharpen your|Sharpen|Fix your|Improve your|Strengthen your)\s+(.+)$/i);
  if (m) return `Complete one ${mins}-minute ${m[1]} mock interview.`;
  const route = mission.route || "";
  if (route.includes("/mock") || route.includes("/practice")) {
    return `Complete one ${mins}-minute focused mock interview.`;
  }
  if (route.includes("/live")) return `Run a ${mins}-minute Official AI Interview.`;
  if (route.includes("/video")) return `Run a ${mins}-minute practice interview.`;
  if (route.includes("/resume")) return `Update your resume in the next ${mins} minutes.`;
  if (route.includes("/briefs")) return `Generate your Interview Brief in the next ${mins} minutes.`;
  if (route.includes("/questions")) return `Generate practice questions in the next ${mins} minutes.`;
  return `Complete this next step in about ${mins} minutes.`;
}

/**
 * InterviewSnapshot - inline gold-accented summary strip placed right below
 * the Welcome subtitle. Renders "date · readiness → projected · duration".
 * All three segments are optional; segments with missing data are skipped so
 * the strip stays clean even on empty accounts.
 */
function InterviewSnapshot({ interviewDate, readinessScore, projectedReadiness, estimatedMinutes, calculating }) {
  const dateLabel = formatInterviewDateLabel(interviewDate);
  const hasReadiness = !calculating && typeof readinessScore === "number";
  const hasProjection = hasReadiness && typeof projectedReadiness === "number" && projectedReadiness !== readinessScore;
  const hasDuration = !!estimatedMinutes;
  // If we have nothing useful, don't render the strip at all.
  if (!dateLabel && !hasReadiness && !hasDuration) return null;

  return (
    <div
      data-testid="interview-snapshot"
      className="mt-4 inline-flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-zinc-300"
    >
      <span className="text-[10px] uppercase tracking-[0.28em] text-amber-400 font-medium">
        Interview Snapshot
      </span>
      <span className="text-zinc-700">·</span>
      {dateLabel && (
        <span className="inline-flex items-center gap-1.5" data-testid="snapshot-date">
          <Calendar className="h-3 w-3 text-amber-400" />
          <span className="text-zinc-100 font-medium">{dateLabel}</span>
          <CountdownChip interviewDate={interviewDate} />
        </span>
      )}
      {dateLabel && (hasReadiness || hasDuration) && <span className="text-zinc-700">•</span>}
      {hasReadiness && (
        <span className="inline-flex items-center gap-1.5" data-testid="snapshot-readiness">
          <Target className="h-3 w-3 text-amber-400" />
          <span className="text-zinc-100 font-medium tabular-nums">{readinessScore}% Ready</span>
          {hasProjection && (
            <>
              <span className="text-zinc-600 mx-0.5">→</span>
              <span className="text-amber-300 font-medium tabular-nums">{projectedReadiness}%</span>
              <span className="text-zinc-500">after your next step</span>
            </>
          )}
        </span>
      )}
      {(hasReadiness || dateLabel) && hasDuration && <span className="text-zinc-700">•</span>}
      {hasDuration && (
        <span className="inline-flex items-center gap-1.5" data-testid="snapshot-duration">
          <Clock className="h-3 w-3 text-amber-400" />
          <span className="text-zinc-100 font-medium tabular-nums">{estimatedMinutes} min</span>
        </span>
      )}
    </div>
  );
}

/**
 * TodaysCoachingCard - a single personalized coaching sentence, fully
 * clickable, that lives beside the Welcome header. When no mocks exist yet,
 * it nudges the user to start their first practice.
 */
function TodaysCoachingCard({ competencies, calculating, readinessScore, todayMission }) {
  const all = Array.isArray(competencies?.all) ? competencies.all : [];
  const strengths = Array.isArray(competencies?.strengths) ? competencies.strengths : [];
  const computedLowest = all.length
    ? all.reduce((a, b) => (a.score <= b.score ? a : b))
    : null;

  // Prefer the competency the Today's Mission is targeting (so Coaching + Mission
  // reinforce each other). Extract it from the mission title; fall back to the
  // computed lowest score when no mission is available.
  const missionTopicMatch = (todayMission?.title || "").match(
    /^(?:Sharpen your|Sharpen|Fix your|Improve your|Strengthen your)\s+(.+)$/i,
  );
  const topicName = missionTopicMatch ? missionTopicMatch[1].trim() : computedLowest?.name;
  const topicScore = topicName
    ? (all.find((c) => c.name.toLowerCase() === topicName.toLowerCase())?.score ?? computedLowest?.score)
    : computedLowest?.score;
  const topic = topicName ? { name: topicName, score: topicScore } : null;

  const isEmpty = calculating || !topic;
  const proj = todayMission?.projected_readiness;
  const cur = readinessScore;

  // Compose ONE personalized coaching sentence - the WHY behind the mission.
  // Frames the recommendation as an opportunity/leverage, never a weakness.
  let message;
  if (isEmpty) {
    message = "Complete your first Practice Interview today and I'll map your fastest path to interview readiness.";
  } else if (strengths.length >= 2 && cur != null && proj != null) {
    const [s1, s2] = strengths;
    message = `You're strong in ${s1.name} and ${s2.name}. Doubling down on ${topic.name} today is your fastest path to lifting readiness from ${cur}% to ${proj}%.`;
  } else if (cur != null && proj != null) {
    message = `${topic.name} is today's biggest lever. One focused mock is projected to lift your readiness from ${cur}% to ${proj}%.`;
  } else {
    message = `${topic.name} is today's biggest lever. Practicing it now is your fastest path to raising interview readiness.`;
  }

  return (
    <Link
      to="/app/practice"
      data-testid="ai-coach-card"
      className="group relative overflow-hidden rounded-2xl border border-violet-500/25 bg-gradient-to-br from-violet-500/[0.06] via-zinc-900/60 to-zinc-950/50 p-5 flex flex-col gap-3 hover:border-violet-500/45 hover:-translate-y-0.5 transition-all duration-200"
    >
      <div className="absolute -top-10 -right-10 h-32 w-32 rounded-full bg-violet-500/10 blur-3xl pointer-events-none group-hover:bg-violet-500/20 transition-colors" />

      <div className="relative flex items-center gap-2">
        <span className="h-6 w-6 rounded-md bg-violet-500/15 border border-violet-500/30 flex items-center justify-center text-sm leading-none" aria-hidden>
          🤖
        </span>
        <span className="text-[10px] uppercase tracking-[0.28em] text-violet-300 font-medium">Today&apos;s Coaching</span>
      </div>

      <div
        className="relative text-sm text-zinc-100 leading-relaxed"
        data-testid="ai-coach-message"
      >
        {message}
      </div>

      <div className="relative mt-auto inline-flex items-center gap-1 text-xs font-medium text-violet-300 group-hover:text-violet-200 transition-colors" data-testid="ai-coach-cta">
        View Coaching
        <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
      </div>
    </Link>
  );
}

function TodaysMissionCard({ mission, calculating, interviewDate }) {
  const showProjection = !calculating && mission?.projected_delta > 0;
  const dateLabel = formatInterviewDateLabel(interviewDate);
  return (
    <div
      data-testid="todays-mission-card"
      className="relative overflow-hidden rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/[0.08] via-zinc-900/50 to-zinc-950/40 p-5 sm:p-6 transition-colors hover:border-amber-500/45"
    >
      <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-amber-500/10 blur-3xl pointer-events-none" />
      <div className="relative grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto] gap-x-6 gap-y-4 items-center">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-amber-400 mb-2">
            <span className="text-sm">🎯</span> Next Step
          </div>
          <div className="text-lg sm:text-xl font-semibold text-zinc-50 leading-snug" data-testid="mission-title">
            {mission.title}
          </div>
          <div className="text-sm text-zinc-400 mt-1.5 leading-relaxed max-w-2xl" data-testid="mission-description">
            {missionActionDescription(mission)}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-1.5 text-[10px] uppercase tracking-widest">
            <span
              className="inline-flex items-center gap-1 text-zinc-300 border border-zinc-700/70 bg-zinc-800/40 rounded-full px-2 py-0.5"
              data-testid="mission-time"
            >
              <Clock className="h-2.5 w-2.5" />
              {mission.estimated_minutes} min
            </span>
            {showProjection && (
              <span
                className="inline-flex items-center gap-1 text-emerald-300 border border-emerald-500/25 bg-emerald-500/[0.06] rounded-full px-2 py-0.5"
                data-testid="mission-projection"
              >
                <TrendingUp className="h-2.5 w-2.5" />
                +{mission.projected_delta} Readiness
              </span>
            )}
            {dateLabel && (
              <span
                className="inline-flex items-center gap-1 text-amber-300 border border-amber-500/30 bg-amber-500/[0.06] rounded-full px-2 py-0.5"
                data-testid="mission-interview-date"
              >
                <Calendar className="h-2.5 w-2.5" />
                {dateLabel}
              </span>
            )}
            {interviewDate && <CountdownChip interviewDate={interviewDate} />}
          </div>
        </div>
        <div className="md:pr-2">
          <Link
            to={mission.route}
            data-testid="mission-cta"
            className="group inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-lg px-5 py-2.5 shadow-[0_0_24px_rgba(245,158,11,0.28)] transition-all hover:-translate-y-0.5 whitespace-nowrap"
          >
            Start Now
            <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>
      </div>
    </div>
  );
}

function InterviewReadinessCard({
  score,
  band,
  trend,
  projectedDelta,
  coachMessage,
  competencies,
  calculating,
  calculatingMessage,
  official,
  readinessSources,
}) {
  // "score" is the preparation-progress signal (blended). "official" carries the Official
  // Interview Readiness (only from Live AI Interview). The card leads with the Official
  // score; when locked, the preparation progress becomes a secondary strip below.
  const isOfficialLocked = !!official?.locked;
  const displayScore = !isOfficialLocked ? official?.score : score;
  const col = bandColor(displayScore || 0);
  const size = 128;
  const stroke = 8;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const safeScore = calculating || isOfficialLocked ? 0 : (typeof displayScore === "number" ? displayScore : 0);
  const offset = c - (Math.max(0, Math.min(100, safeScore)) / 100) * c;
  const hasTrend = typeof trend === "number" && trend !== 0 && !calculating && !isOfficialLocked;
  const strengths = competencies?.strengths || [];
  const focus = competencies?.needs_improvement || [];

  const navigate = useNavigate();
  const { isFree, refresh: refreshPlan } = useBillingPlan();
  const { unlockScoreMode, track } = useEntitlement();
  const [showUpgrade, setShowUpgrade] = useState(false);

  // Fire dashboard.locked_score_viewed once when the Official Readiness card
  // renders in a locked state - the moment a free user sees the "•• / 100"
  // preview or "Not Yet Assessed" state.
  useEffect(() => {
    if (isOfficialLocked) {
      track("dashboard.locked_score_viewed", {
        mode: unlockScoreMode,
        locked_reason: official?.locked_reason || null,
      });
    }
  }, [isOfficialLocked, track, unlockScoreMode, official?.locked_reason]);

  const handleLiveClick = (e) => {
    if (isFree) {
      e.preventDefault();
      setShowUpgrade(true);
    }
  };

  return (
    <div
      data-testid="interview-readiness-card"
      className="relative overflow-hidden rounded-2xl border border-zinc-800/70 bg-zinc-900/40 p-6 flex flex-col"
    >
      <UpgradeLiveModal
        open={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        onActivated={async () => {
          await refreshPlan();
          setShowUpgrade(false);
          navigate("/app/live");
        }}
      />
      <div className="absolute -top-24 -right-24 h-56 w-56 rounded-full bg-amber-500/[0.03] blur-3xl pointer-events-none" />
      <div className="relative flex items-center justify-between mb-4">
        <div className="text-[11px] uppercase tracking-[0.32em] text-zinc-300 font-medium inline-flex items-center gap-2">
          {isOfficialLocked && <Lock className="h-3 w-3 text-zinc-500" />}
          Official Interview Readiness
          <ScoreInfoTip variant="readiness" />
        </div>
        {hasTrend && (
          <span
            data-testid="readiness-trend-chip"
            className={`inline-flex items-center gap-1 text-[10px] font-semibold tabular-nums px-1.5 py-0.5 rounded-md border ${
              trend > 0
                ? "text-emerald-300 border-emerald-500/25 bg-emerald-500/[0.05]"
                : "text-rose-300 border-rose-500/25 bg-rose-500/[0.05]"
            }`}
          >
            {trend > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
            {trend > 0 ? `+${trend}` : `${trend}`}
          </span>
        )}
        {!hasTrend && official?.score != null && official?.level && (
          <span
            data-testid="readiness-level-chip"
            className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full border ${col.border} ${col.bg} ${col.text}`}
          >
            {official.level}
          </span>
        )}
      </div>

      <div className="relative grid grid-cols-[auto_1fr] gap-5 items-center">
        {/* Gauge */}
        <div className="relative flex items-center justify-center flex-shrink-0" style={{ width: size, height: size }}>
          <svg width={size} height={size} className="-rotate-90">
            <circle cx={size / 2} cy={size / 2} r={r} stroke="#27272A" strokeWidth={stroke} fill="transparent" />
            <circle
              cx={size / 2}
              cy={size / 2}
              r={r}
              stroke={col.ring}
              strokeWidth={stroke}
              strokeLinecap="round"
              fill="transparent"
              strokeDasharray={c}
              strokeDashoffset={offset}
              style={{ transition: "stroke-dashoffset 1000ms ease-out" }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            {isOfficialLocked ? (
              unlockScoreMode === "preview" ? (
                <>
                  <div
                    data-testid="readiness-preview-placeholder"
                    className="text-3xl font-bold tracking-tight text-zinc-500 leading-none select-none"
                  >
                    ••<span className="text-base text-zinc-600 font-medium">/100</span>
                  </div>
                  <div className="text-[9px] uppercase tracking-[0.22em] text-amber-400/80 mt-1.5">
                    Preview
                  </div>
                </>
              ) : (
                <>
                  <Lock className="h-5 w-5 text-zinc-500 mb-1" />
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-medium leading-tight text-center">
                    Not Yet<br/>Assessed
                  </div>
                </>
              )
            ) : calculating ? (
              <div className="text-[11px] text-zinc-200 font-semibold text-center leading-tight px-2">
                Calculating…
              </div>
            ) : (
              <>
                <div className="text-3xl font-bold tracking-tight text-zinc-50 leading-none">
                  <AnimatedNumber value={displayScore} />
                  <span className="text-base text-zinc-500 font-medium">%</span>
                </div>
                <div className={`text-[10px] mt-1.5 font-semibold ${col.text}`} data-testid="readiness-band-label">
                  {band}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Right pane, projected + coaching summary */}
        <div className="min-w-0 space-y-3">
          {isOfficialLocked ? (
            <div data-testid="official-readiness-locked" className="space-y-3">
              <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.28em] text-amber-400 border border-amber-500/30 bg-amber-500/[0.06] rounded-full px-2.5 py-1">
                <Trophy className="h-3 w-3" />
                Official Readiness Score
              </div>
              <div className="text-sm text-zinc-100 leading-relaxed font-medium">
                Complete one Official AI Interview to earn your verified score.
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-1.5">
                  You&apos;ll receive:
                </div>
                <ul className="grid grid-cols-1 gap-y-1 text-xs text-zinc-300">
                  {[
                    "Overall Readiness Score",
                    "Hiring Confidence Level",
                    "Strength Analysis",
                    "Improvement Areas",
                    "Interview Report",
                  ].map((f) => (
                    <li key={f} className="flex items-start gap-1.5">
                      <Check className="h-3 w-3 text-amber-400 mt-0.5 flex-shrink-0" strokeWidth={3} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <Link
                to="/app/live"
                onClick={handleLiveClick}
                data-testid="official-readiness-cta"
                className="mt-1 inline-flex items-center gap-2 text-xs font-semibold bg-amber-500 hover:bg-amber-400 text-amber-950 rounded-lg px-3.5 py-2 shadow-[0_0_24px_-8px_rgba(245,158,11,0.6)] transition-colors group"
              >
                Get my official score
                <span
                  data-testid="official-readiness-cta-pro"
                  className="text-[9px] uppercase tracking-widest text-amber-950/80 border border-amber-950/30 rounded-full px-1.5 py-0.5"
                >
                  Pro
                </span>
                <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>
          ) : calculating ? (
            <div
              data-testid="readiness-calculating-message"
              className="text-sm text-zinc-100 leading-relaxed"
            >
              {calculatingMessage || "Complete your first Practice Interview to unlock your Interview Readiness Score."}
            </div>
          ) : (
            <>
              {projectedDelta > 0 && (
                <div className="text-xs text-zinc-300 leading-relaxed" data-testid="readiness-projection-line">
                  <span className={`${col.text} font-semibold`}>+{projectedDelta}</span>{" "}
                  <span className="text-zinc-500">projected after next practice</span>
                </div>
              )}
              {coachMessage && (
                <div className="text-xs text-zinc-300 leading-relaxed" data-testid="readiness-coach-summary">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-amber-400/80 block mb-1">
                    AI Coaching Summary
                  </span>
                  {coachMessage}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Preparation Progress strip, shown when Official is locked so users still see what they've built. */}
      {isOfficialLocked && (
        <div className="mt-5 pt-5 border-t border-zinc-800/60" data-testid="preparation-progress">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 font-medium">
              Preparation Progress
            </div>
            {typeof score === "number" && (
              <div className="text-lg font-bold text-zinc-100 tabular-nums" data-testid="preparation-progress-score">
                {score}%
              </div>
            )}
          </div>
          {typeof score === "number" && (
            <div className="h-1.5 rounded-full bg-zinc-800 overflow-hidden mb-3">
              <div className="h-full bg-gradient-to-r from-emerald-500/80 to-amber-500/80" style={{ width: `${Math.min(100, score)}%` }} />
            </div>
          )}
          {Array.isArray(readinessSources) && readinessSources.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="preparation-progress-sources">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500 mr-1">Based on</span>
              {readinessSources.map((s) => (
                <span
                  key={s.key}
                  data-testid={`preparation-source-${s.key}`}
                  className={`inline-flex items-center gap-1 text-[10px] rounded-full px-2 py-0.5 border ${
                    s.done
                      ? "text-emerald-300 border-emerald-500/25 bg-emerald-500/[0.06]"
                      : "text-zinc-500 border-zinc-800 bg-zinc-900/50"
                  }`}
                >
                  {s.done ? <Check className="h-2.5 w-2.5" /> : <Clock className="h-2.5 w-2.5" />}
                  {s.label}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Strengths + focus chips */}
      {!calculating && (strengths.length > 0 || focus.length > 0) && (
        <div className="mt-5 pt-5 border-t border-zinc-800/60 space-y-3">
          {strengths.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-emerald-300/80 mb-2">
                Your Strengths
              </div>
              <div className="flex flex-wrap gap-1.5" data-testid="readiness-strengths">
                {strengths.slice(0, 3).map((s) => (
                  <span
                    key={s.name}
                    className="text-[11px] px-2 py-0.5 rounded-md border border-emerald-500/25 bg-emerald-500/[0.06] text-emerald-200 inline-flex items-center gap-1.5"
                  >
                    {s.name} <span className="text-emerald-400 tabular-nums">{s.score}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
          {focus.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-amber-400/80 mb-2">
                Next Focus
              </div>
              <div className="flex flex-wrap gap-1.5" data-testid="readiness-focus">
                {focus.slice(0, 3).map((s) => (
                  <span
                    key={s.name}
                    className="text-[11px] px-2 py-0.5 rounded-md border border-amber-500/25 bg-amber-500/[0.05] text-amber-200 inline-flex items-center gap-1.5"
                  >
                    {s.name} <span className="text-amber-400 tabular-nums">{s.score}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {calculating && (
        <Link
          to="/app/practice?step=video"
          data-testid="readiness-calculating-cta"
          className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold text-amber-300 hover:text-amber-200 transition-colors"
        >
          Start Practice Interview <ArrowRight className="h-3 w-3" />
        </Link>
      )}
    </div>
  );
}

function FastestPathCard({ fastest, calculating }) {
  if (calculating) {
    return (
      <div
        data-testid="fastest-path-card"
        data-state="calculating"
        className="rounded-2xl border border-zinc-800/70 bg-zinc-900/40 p-6 flex flex-col justify-between"
      >
        <div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-amber-400 mb-2 inline-flex items-center gap-1.5">
            <span className="text-sm">🚀</span> Fastest Path
          </div>
          <div className="text-sm text-zinc-400 leading-relaxed">
            Recommendations unlock once you complete your first Practice Interview.
          </div>
        </div>
      </div>
    );
  }
  if (!fastest) {
    return (
      <div
        data-testid="fastest-path-card"
        data-state="empty"
        className="rounded-2xl border border-zinc-800/70 bg-zinc-900/40 p-6 flex flex-col justify-between"
      >
        <div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-amber-400 mb-2 inline-flex items-center gap-1.5">
            <span className="text-sm">🚀</span> Fastest Path
          </div>
          <div className="text-sm text-zinc-400 leading-relaxed">
            You&apos;re running lean. Keep practicing to stay sharp for interview day.
          </div>
        </div>
      </div>
    );
  }

  // Derived "time to complete" per action_type - mirrors the effort implied by
  // the recommendation so users know what they're committing to.
  const TIME_BY_ACTION = {
    improve_alignment: 10,
    upload_resume: 5,
    generate_questions: 5,
    practice_mock: 15,
    finish_mock: 10,
    video_mock: 20,
    live_interview: 25,
    generate_brief: 5,
    review_brief: 8,
  };
  // Derived "next unlock" - what this action unblocks in the interview journey.
  const UNLOCK_BY_ACTION = {
    improve_alignment: "Unlock stronger match score",
    upload_resume: "Unlock resume analysis",
    generate_questions: "Unlock Practice Interview",
    practice_mock: "Unlock Interview Brief",
    finish_mock: "Unlock Interview Brief",
    video_mock: "Unlock Official AI Interview",
    live_interview: "Interview Ready",
    generate_brief: "Interview Ready",
    review_brief: "Interview Ready",
  };
  const minutes = fastest.estimated_minutes || TIME_BY_ACTION[fastest.action_type] || 15;
  const unlockLabel = fastest.next_unlock || UNLOCK_BY_ACTION[fastest.action_type] || "Unlock next step";

  return (
    <Link
      to={fastest.route || "/app/practice"}
      data-testid="fastest-path-card"
      className="group relative overflow-hidden rounded-2xl border border-amber-500/25 bg-gradient-to-br from-amber-500/[0.05] via-zinc-900/40 to-zinc-950/60 p-6 flex flex-col justify-between hover:border-amber-500/45 hover:-translate-y-0.5 transition-all duration-200"
    >
      <div className="absolute -bottom-16 -right-16 h-40 w-40 rounded-full bg-amber-500/10 blur-3xl pointer-events-none group-hover:bg-amber-500/20 transition-colors" />
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] uppercase tracking-[0.28em] text-amber-400 inline-flex items-center gap-1.5">
            <span className="text-sm">🚀</span> Fastest Path
          </div>
          {fastest.premium && (
            <span
              data-testid="fastest-path-card-pro"
              className="text-[9px] uppercase tracking-widest text-amber-400 border border-amber-500/30 bg-amber-500/[0.06] rounded-full px-1.5 py-0.5"
            >
              Pro
            </span>
          )}
        </div>
        <div
          data-testid="fastest-path-card-label"
          className="text-base font-semibold text-zinc-50 leading-snug"
        >
          {fastest.label}
        </div>

        {/* Chips: time · gain · unlock */}
        <div className="flex flex-wrap items-center gap-1.5 mt-3" data-testid="fastest-path-chips">
          <span
            data-testid="fastest-path-chip-time"
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-300 border border-zinc-700/70 bg-zinc-800/40 rounded-full px-2 py-0.5"
          >
            <Clock className="h-2.5 w-2.5" />
            {minutes} min
          </span>
          <span
            data-testid="fastest-path-chip-gain"
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-emerald-300 border border-emerald-500/25 bg-emerald-500/[0.06] rounded-full px-2 py-0.5"
          >
            <TrendingUp className="h-2.5 w-2.5" />
            +{fastest.projected_gain} Readiness
          </span>
          <span
            data-testid="fastest-path-chip-unlock"
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-amber-300 border border-amber-500/30 bg-amber-500/[0.06] rounded-full px-2 py-0.5"
          >
            <Unlock className="h-2.5 w-2.5" />
            {unlockLabel}
          </span>
        </div>
      </div>
      <div className="relative mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-amber-300 group-hover:text-amber-200 transition-colors">
        Take action
        <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
      </div>
    </Link>
  );
}


function InterviewContextCard({ ctx, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [date, setDate] = useState(ctx?.interview_date ? ctx.interview_date.slice(0, 16) : "");
  const [company, setCompany] = useState(ctx?.company || "");
  const [role, setRole] = useState(ctx?.role || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDate(ctx?.interview_date ? ctx.interview_date.slice(0, 16) : "");
    setCompany(ctx?.company || "");
    setRole(ctx?.role || "");
  }, [ctx]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        interview_date: date || null,
        company: company || null,
        role: role || null,
      };
      const { data } = await api.post("/user/interview-context", payload);
      onUpdate(data);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const hasDate = !!ctx?.interview_date;
  const dateDisplay = hasDate
    ? new Date(ctx.interview_date).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })
    : null;

  const daysUntil = hasDate
    ? Math.ceil((new Date(ctx.interview_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <div
      data-testid="interview-context-card"
      className="rounded-xl border border-zinc-800/70 bg-zinc-900/40 p-5"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-zinc-500">
          <Calendar className="h-3 w-3" /> Upcoming Interview
        </div>
        {!editing && (
          <button
            data-testid="interview-context-edit-btn"
            onClick={() => setEditing(true)}
            className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-200 inline-flex items-center gap-1"
          >
            <Edit3 className="h-3 w-3" /> Edit
          </button>
        )}
      </div>

      {editing ? (
        <div className="space-y-2">
          <input
            data-testid="interview-context-date-input"
            type="datetime-local"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-50 focus:outline-none focus:border-zinc-700"
          />
          <input
            data-testid="interview-context-company-input"
            type="text"
            placeholder="Company"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-50 focus:outline-none focus:border-zinc-700"
          />
          <input
            data-testid="interview-context-role-input"
            type="text"
            placeholder="Role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-50 focus:outline-none focus:border-zinc-700"
          />
          <div className="flex gap-2 pt-1">
            <button
              data-testid="interview-context-save-btn"
              onClick={save}
              disabled={saving}
              className="text-xs bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-md px-3 py-1.5"
            >
              {saving ? "Saving…" : "Save"}
            </button>
            <button
              onClick={() => setEditing(false)}
              className="text-xs border border-zinc-800 text-zinc-400 rounded-md px-3 py-1.5"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : hasDate || ctx?.role || ctx?.company ? (
        <div className="space-y-1.5">
          {hasDate && (
            <div>
              <div className="text-base font-semibold text-zinc-50">{dateDisplay}</div>
              {daysUntil !== null && daysUntil >= 0 && (
                <div className="text-xs text-amber-400 mt-0.5">
                  {daysUntil === 0 ? "Today" : daysUntil === 1 ? "Tomorrow" : `In ${daysUntil} days`}
                </div>
              )}
            </div>
          )}
          {(ctx?.role || ctx?.company) && (
            <div className="text-sm text-zinc-400 leading-relaxed">
              {ctx.role}
              {ctx.role && ctx.company ? " · " : ""}
              {ctx.company}
            </div>
          )}
        </div>
      ) : (
        <div className="text-xs text-zinc-500 leading-relaxed">
          Add your interview date to see a countdown and get prep timed to it.
        </div>
      )}
    </div>
  );
}


/* ------------------------------------------------------------------
 * InterviewReadyJourneyCard - the 6-step conversion path (P2 spec).
 * Emphasizes progress toward getting hired, not feature usage.
 * Free users see ✅ on completed free steps and 🔒 on the two premium
 * steps. Tapping a locked step navigates to /app/upgrade (fires an
 * analytics event first). Uses `data.interview_ready_journey` from
 * the dashboard summary.
 * ------------------------------------------------------------------ */
function InterviewReadyJourneyCard({ journey, premium }) {
  const navigate = useNavigate();
  const { track } = useEntitlement();
  const steps = journey?.steps || [];
  const total = steps.length || 6;
  const doneCount = steps.filter((s) => s.done).length;
  const progressPct = total > 0 ? Math.round((doneCount / total) * 100) : 0;
  const complete = doneCount >= total;

  const handleLockedClick = (stepKey) => {
    track("dashboard.journey_locked_step_clicked", { step: stepKey });
    navigate("/app/upgrade");
  };

  // Human-friendly "3 days ago" style formatter - kept locally so the
  // Journey card doesn't pull in a heavier date lib.
  const formatCompleted = (iso) => {
    if (!iso) return null;
    const t = new Date(iso).getTime();
    if (!Number.isFinite(t)) return null;
    const diffMs = Date.now() - t;
    const sec = Math.floor(diffMs / 1000);
    if (sec < 45) return "just now";
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min} min${min === 1 ? "" : "s"} ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr} hour${hr === 1 ? "" : "s"} ago`;
    const day = Math.floor(hr / 24);
    if (day < 7) return `${day} day${day === 1 ? "" : "s"} ago`;
    if (day < 30) {
      const wk = Math.floor(day / 7);
      return `${wk} week${wk === 1 ? "" : "s"} ago`;
    }
    if (day < 365) {
      const mo = Math.floor(day / 30);
      return `${mo} month${mo === 1 ? "" : "s"} ago`;
    }
    const yr = Math.floor(day / 365);
    return `${yr} year${yr === 1 ? "" : "s"} ago`;
  };

  return (
    <div
      data-testid="interview-ready-journey"
      className="relative overflow-hidden rounded-2xl border border-zinc-800/70 bg-zinc-900/40 p-5 sm:p-6"
    >
      <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-amber-500/[0.04] blur-3xl pointer-events-none" />

      <div className="relative flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.28em] text-amber-400 font-medium inline-flex items-center gap-1.5">
          <Trophy className="h-3 w-3" /> Journey to Interview Ready
        </div>
        <div
          data-testid="irj-count"
          className="text-[10px] uppercase tracking-widest tabular-nums text-zinc-400"
        >
          <span className="text-zinc-100 font-semibold">{doneCount}</span> of {total} steps
        </div>
      </div>

      {/* Progress bar */}
      <div className="relative">
        <div className="h-1.5 rounded-full bg-zinc-800 overflow-hidden">
          <div
            data-testid="irj-progress-bar"
            className={`h-full transition-all duration-700 ease-out ${
              complete
                ? "bg-gradient-to-r from-emerald-500 to-amber-400"
                : "bg-gradient-to-r from-amber-500/80 to-amber-400/90"
            }`}
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <div
          data-testid="irj-caption"
          className="mt-2 text-xs text-zinc-300 leading-relaxed"
        >
          {complete ? (
            <span className="text-amber-300 font-medium">
              You&apos;re Interview Ready. Congratulations!
            </span>
          ) : (
            <>
              You&apos;re{" "}
              <span className="text-amber-300 font-semibold tabular-nums">
                {doneCount} of {total}
              </span>{" "}
              steps toward Interview Ready.
            </>
          )}
        </div>
      </div>

      {/* Steps list */}
      <ol className="mt-5 space-y-1.5">
        {steps.map((s) => {
          // A step is "locked" only when it's a premium step, not yet done,
          // AND the current user is NOT premium. Paying users see the same
          // step as a passive "upcoming" row (no button, no upgrade route).
          const locked = s.premium && !s.done && !premium;
          const state = s.done ? "done" : locked ? "locked" : "upcoming";
          const commonClasses =
            "group flex items-center gap-3 rounded-lg px-2 py-2.5 -mx-2 transition-all duration-150";

          const inner = (
            <>
              <IRJStepIndicator state={state} />
              <div className="flex-1 min-w-0">
                <span
                  className={[
                    "block text-sm truncate",
                    state === "done" && "text-zinc-100",
                    state === "locked" && "text-zinc-400",
                    state === "upcoming" && "text-zinc-300",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {s.label}
                </span>
                {state === "done" && s.completed_at && (
                  <span
                    data-testid={`irj-step-${s.key}-completed-at`}
                    title={new Date(s.completed_at).toLocaleString()}
                    className="block text-[10px] uppercase tracking-widest text-emerald-400/70 mt-0.5 tabular-nums"
                  >
                    Completed {formatCompleted(s.completed_at)}
                  </span>
                )}
              </div>
              {s.premium && (
                <span
                  className={`text-[9px] uppercase tracking-widest rounded-full px-1.5 py-0.5 border ${
                    s.done
                      ? "text-emerald-300 border-emerald-500/25 bg-emerald-500/[0.06]"
                      : "text-amber-400 border-amber-500/30 bg-amber-500/[0.06]"
                  }`}
                >
                  Premium
                </span>
              )}
              {locked && (
                <ArrowRight className="h-3 w-3 text-amber-400 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
              )}
            </>
          );

          if (locked) {
            return (
              <li key={s.key}>
                <button
                  type="button"
                  onClick={() => handleLockedClick(s.key)}
                  data-testid={`irj-step-${s.key}`}
                  data-state={state}
                  className={`${commonClasses} w-full text-left hover:bg-amber-500/[0.04] hover:pl-3 cursor-pointer`}
                >
                  {inner}
                </button>
              </li>
            );
          }
          return (
            <li key={s.key}>
              <div
                data-testid={`irj-step-${s.key}`}
                data-state={state}
                className={`${commonClasses} ${state === "done" ? "" : "opacity-90"}`}
              >
                {inner}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function IRJStepIndicator({ state }) {
  if (state === "done") {
    return (
      <span className="h-6 w-6 rounded-full bg-emerald-500 border border-emerald-400 text-emerald-950 flex items-center justify-center flex-shrink-0 shadow-[0_0_12px_-4px_rgba(16,185,129,0.6)]">
        <Check className="h-3.5 w-3.5" strokeWidth={3} />
      </span>
    );
  }
  if (state === "locked") {
    return (
      <span className="h-6 w-6 rounded-full bg-zinc-950 border border-amber-500/40 flex items-center justify-center flex-shrink-0">
        <Lock className="h-3 w-3 text-amber-400/80" />
      </span>
    );
  }
  return (
    <span className="h-6 w-6 rounded-full bg-zinc-950 border border-zinc-700 flex items-center justify-center flex-shrink-0">
      <span className="h-1.5 w-1.5 rounded-full bg-zinc-600" />
    </span>
  );
}

/* ------------------------------------------------------------------
 * LockedReplaysCard - teases 3 blurred replay thumbnails for Free
 * users. Every click opens the Upgrade Screen (with analytics).
 * Hidden entirely for premium users.
 * ------------------------------------------------------------------ */
function LockedReplaysCard() {
  const navigate = useNavigate();
  const { track } = useEntitlement();

  const REPLAYS = [
    { key: "video_mock", label: "Practice Interview Replay", accent: "from-amber-500/25 to-amber-400/10" },
    { key: "live_interview", label: "Official AI Interview Replay", accent: "from-violet-500/25 to-violet-400/10" },
    { key: "coaching_review", label: "AI Coaching Review", accent: "from-emerald-500/25 to-emerald-400/10" },
  ];

  const handleClick = (replayKey) => {
    track("dashboard.replay_locked_clicked", { replay: replayKey });
    navigate("/app/upgrade");
  };

  return (
    <div
      data-testid="locked-replays-card"
      className="rounded-2xl border border-zinc-800/70 bg-zinc-900/40 p-5 sm:p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="text-[10px] uppercase tracking-[0.28em] text-amber-400 font-medium inline-flex items-center gap-1.5">
          <Video className="h-3 w-3" /> Your Interview Replays
        </div>
        <span className="text-[9px] uppercase tracking-widest text-amber-400 border border-amber-500/30 bg-amber-500/[0.06] rounded-full px-1.5 py-0.5">
          Premium
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {REPLAYS.map((r) => (
          <button
            type="button"
            key={r.key}
            onClick={() => handleClick(r.key)}
            data-testid={`locked-replay-${r.key}`}
            className="group relative aspect-video overflow-hidden rounded-xl border border-zinc-800/80 bg-zinc-950 hover:border-amber-500/40 transition-colors"
          >
            {/* Blurred fake thumbnail */}
            <div
              aria-hidden
              className={`absolute inset-0 bg-gradient-to-br ${r.accent} opacity-80`}
              style={{ filter: "blur(14px)" }}
            />
            <div
              aria-hidden
              className="absolute inset-0 opacity-40"
              style={{
                background:
                  "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.15), transparent 55%), radial-gradient(circle at 70% 70%, rgba(0,0,0,0.4), transparent 55%)",
                filter: "blur(10px)",
              }}
            />
            {/* Dark overlay for legibility */}
            <div className="absolute inset-0 bg-black/45" />

            {/* Lock + label */}
            <div className="relative h-full flex flex-col items-center justify-center gap-2 text-center px-2">
              <div className="h-9 w-9 rounded-full border border-amber-500/40 bg-amber-500/[0.10] flex items-center justify-center backdrop-blur-sm">
                <Lock className="h-4 w-4 text-amber-300" />
              </div>
              <div className="text-xs text-zinc-100 font-medium">{r.label}</div>
              <div className="text-[10px] uppercase tracking-widest text-amber-300/90 opacity-0 group-hover:opacity-100 transition-opacity">
                Unlock →
              </div>
            </div>
          </button>
        ))}
      </div>

      <div className="mt-4 text-xs text-zinc-400 leading-relaxed">
        Rewatch every Practice Interview and Official AI Interview with AI coaching notes overlaid frame-by-frame, available on Premium.
      </div>
    </div>
  );
}



function LatestBriefCard({ brief }) {
  if (!brief) {
    return (
      <Link
        to="/app/practice?step=chat"
        data-testid="card-latest-brief-empty"
        className="group relative overflow-hidden rounded-xl border border-zinc-800/70 bg-zinc-900/40 p-5 hover:border-zinc-700 transition-colors block"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">Latest Interview Brief</div>
        </div>
        <div className="flex items-center gap-3">
          <span className="h-9 w-9 rounded-lg bg-zinc-900 border border-zinc-800 flex items-center justify-center flex-shrink-0">
            <Sparkles className="h-4 w-4 text-zinc-500" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-zinc-100">Complete your first mock to unlock</div>
            <div className="text-[11px] text-zinc-500 mt-0.5">
              We&apos;ll auto-generate a 1-pager with your best stories and reminders.
            </div>
          </div>
          <ArrowRight className="h-4 w-4 text-zinc-600 group-hover:text-zinc-300 transition-colors flex-shrink-0" />
        </div>
      </Link>
    );
  }

  const title = [brief.role, brief.company].filter(Boolean).join(" @ ") || "Your interview brief";

  return (
    <Link
      to={`/app/briefs?id=${encodeURIComponent(brief.cheat_sheet_id)}`}
      data-testid="card-latest-brief"
      className="group relative overflow-hidden rounded-xl border border-zinc-800/70 bg-zinc-900/40 p-5 hover:border-amber-500/30 transition-colors block"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.24em] text-amber-400 inline-flex items-center gap-1.5">
          <Sparkles className="h-3 w-3" /> Latest Interview Brief
        </div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">
          {timeAgo(brief.created_at)}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <span className="h-9 w-9 rounded-lg bg-amber-500/10 border border-amber-500/25 flex items-center justify-center flex-shrink-0">
          <Sparkles className="h-4 w-4 text-amber-400" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-zinc-50 truncate">{title}</div>
          <div className="text-[11px] text-zinc-500 mt-0.5">Open your 1-pager for interview day</div>
        </div>
        <ChevronRight className="h-4 w-4 text-zinc-500 group-hover:text-amber-400 transition-colors flex-shrink-0" />
      </div>
    </Link>
  );
}

function ReadinessForecastCard({ forecast }) {
  const { current, projected, projected_gain, estimated_minutes, steps, complete } = forecast;
  const col = bandColor(projected || 0);

  if (complete || projected_gain <= 0) {
    return (
      <div
        data-testid="readiness-forecast-card"
        data-state="complete"
        className="rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.03] p-6 flex items-center gap-4"
      >
        <div className="h-12 w-12 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center flex-shrink-0">
          <Trophy className="h-5 w-5 text-emerald-300" />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.24em] text-emerald-300 mb-1">You&apos;re on the finish line</div>
          <div className="text-sm text-zinc-100">
            You&apos;ve completed every practice step for your current readiness ceiling. Run additional mocks to keep sharpening your delivery.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="readiness-forecast-card"
      className="relative overflow-hidden rounded-2xl border border-zinc-800/70 bg-gradient-to-br from-amber-500/[0.04] via-zinc-900/50 to-zinc-950/40 p-6"
    >
      <div className="absolute -bottom-20 -right-16 h-56 w-56 rounded-full bg-amber-500/10 blur-3xl pointer-events-none" />

      {/* Top row, Current → Projected header (compact, self-contained) */}
      <div className="relative flex flex-wrap items-center gap-x-6 gap-y-3 mb-5 pb-5 border-b border-zinc-800/60">
        <div className="inline-flex items-center gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 mb-0.5">Current</div>
            <div className="text-2xl font-bold tracking-tight text-zinc-100 tabular-nums leading-none" data-testid="forecast-current">
              {current}%
            </div>
          </div>
          <ArrowRight className="h-4 w-4 text-amber-400/70 flex-shrink-0" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-amber-400 mb-0.5">Projected</div>
            <div className={`text-3xl font-bold tracking-tight tabular-nums leading-none ${col.text}`} data-testid="forecast-projected">
              {projected}%
            </div>
          </div>
        </div>
        <div className="text-[11px] text-amber-300 font-semibold tabular-nums inline-flex items-center gap-1.5" data-testid="forecast-gain">
          <TrendingUp className="h-3 w-3" />
          +{projected_gain} readiness · ~{estimated_minutes} min total
        </div>
      </div>

      {/* Two-column: action steps (left, expanded) + expected outcome (right) */}
      <div className="relative grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,17rem)] gap-6">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 mb-3">Complete these steps</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {steps.map((s) => (
              <Link
                key={s.key}
                to={s.premium ? "/app/account/billing" : s.route}
                data-testid={`forecast-step-${s.key}`}
                className="group flex items-center gap-3 rounded-lg border border-zinc-800/70 bg-zinc-950/50 hover:border-amber-500/40 hover:bg-amber-500/[0.03] hover:-translate-y-0.5 px-3 py-2.5 transition-all duration-150"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-zinc-100 font-medium truncate">{s.label}</div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 inline-flex items-center gap-1.5 mt-0.5">
                    <Clock className="h-2.5 w-2.5" /> ~{s.estimated_minutes} min
                    {s.premium && <span className="ml-1 text-amber-400">· Pro</span>}
                  </div>
                </div>
                <ArrowRight className="h-3 w-3 text-zinc-500 group-hover:text-amber-400 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
              </Link>
            ))}
          </div>
        </div>

        {/* Expected outcome, reinforces the WHY the projected % matters */}
        <div className="min-w-0 lg:pl-6 lg:border-l lg:border-zinc-800/60">
          <div className="text-[10px] uppercase tracking-[0.24em] text-amber-400 mb-3">Expected Outcome</div>
          <ul className="space-y-2" data-testid="forecast-expected-outcome">
            {[
              { icon: Sparkles, label: "Interview Brief Ready" },
              { icon: TrendingUp, label: "Higher Confidence" },
              { icon: Trophy, label: "Ready for Interview" },
            ].map(({ icon: Icon, label }) => (
              <li key={label} className="flex items-center gap-2.5 text-sm text-zinc-200">
                <span className="h-6 w-6 rounded-md bg-amber-500/[0.08] border border-amber-500/20 flex items-center justify-center flex-shrink-0">
                  <Icon className="h-3 w-3 text-amber-400" strokeWidth={2.5} />
                </span>
                {label}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function MetricTile({ testid, label, value, subValue, tone = "default" }) {
  const toneMap = {
    default: "border-zinc-800/70 bg-zinc-900/40",
    strong: "border-emerald-500/20 bg-emerald-500/5",
    focus: "border-amber-500/20 bg-amber-500/5",
  };
  return (
    <div
      data-testid={testid}
      className={`p-4 rounded-xl border ${toneMap[tone]} hover:border-zinc-700 transition-colors`}
    >
      <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">{label}</div>
      <div className="mt-2 text-2xl font-bold tracking-tight text-zinc-50 tabular-nums">
        {value === null || value === undefined ? <span className="text-zinc-600 text-base font-normal">–</span> : value}
      </div>
      {subValue && <div className="text-[11px] text-zinc-500 mt-1">{subValue}</div>}
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function Dashboard() {
  const { user } = useAuth();
  if (user?.is_guest) return <GuestGate feature="track your readiness score, progress and interview history" />;
  return <DashboardInner />;
}

function DashboardInner() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const load = async () => {
    setLoadError(null);
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/summary");
      setData(data);
    } catch (e) {
      // Transient upstream hiccups (502 during a hot-reload, brief network
      // blip) shouldn't crash the whole page with an uncaught promise
      // rejection. Surface a friendly retry card instead.
      const status = e?.response?.status;
      const msg = status === 502
        ? "The server is warming up. Give it a moment and retry."
        : "We couldn't load your dashboard right now.";
      setLoadError({ status, message: msg });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Refetch whenever the user switches workspace so the checklist, scores,
    // and journey progress always reflect the selected position - not what
    // they finished for a different job.
    const off = onWorkspaceChange(() => { load(); });
    return () => { off(); };
  }, []);

  const firstName = data?.user?.first_name || (user?.name || "").split(" ")[0] || "there";

  if (loading) {
    return (
      <div className="animate-in text-zinc-500 text-sm" data-testid="dashboard-loading">
        Preparing your dashboard&hellip;
      </div>
    );
  }
  if (loadError) {
    return (
      <div
        data-testid="dashboard-error"
        className="animate-in max-w-lg rounded-2xl border border-rose-500/30 bg-gradient-to-br from-rose-500/[0.06] via-zinc-900/40 to-zinc-950/60 p-6"
      >
        <div className="text-sm font-semibold text-zinc-100">
          {loadError.status ? `Dashboard is temporarily unavailable (${loadError.status})` : "Dashboard is temporarily unavailable"}
        </div>
        <div className="text-[13px] text-zinc-400 mt-1 leading-relaxed">{loadError.message}</div>
        <button
          type="button"
          onClick={load}
          data-testid="dashboard-retry-btn"
          className="mt-4 inline-flex items-center gap-2 rounded-lg border border-amber-400/40 bg-amber-400/10 text-amber-200 hover:bg-amber-400/20 px-3.5 py-2 text-sm font-medium transition-colors"
        >
          Try again
        </button>
      </div>
    );
  }
  if (!data) return null;

  const cm = data.career_metrics;

  return (
    <div data-testid="dashboard-page" className="animate-in space-y-8">
      {/* Header, Welcome + AI Coach side-by-side */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 lg:gap-8 lg:items-stretch">
        <div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-amber-400 mb-2">Dashboard</div>
          <h1 className="text-3xl font-bold tracking-tight text-zinc-50">
            Welcome back, {firstName}.
          </h1>
          <p className="text-sm text-zinc-400 mt-2 max-w-2xl">
            Know where you stand. Know what to do next. See your current progress.
          </p>
          <InterviewSnapshot
            interviewDate={data.interview_context?.interview_date}
            readinessScore={data.readiness_score}
            projectedReadiness={data.today_mission?.projected_readiness}
            estimatedMinutes={data.today_mission?.estimated_minutes}
            calculating={data.readiness_calculating}
          />
        </div>
        <div className="lg:w-80 lg:max-w-[22rem]">
          <TodaysCoachingCard
            competencies={data.competencies}
            calculating={data.readiness_calculating}
            readinessScore={data.readiness_score}
            todayMission={data.today_mission}
          />
        </div>
      </div>

      {/* Main mission-control content, full width. The 01-05 journey lives
          only in the app sidebar; the dashboard never duplicates it. */}
      <div className="min-w-0 space-y-8">
      {/* 🎯 TODAY'S MISSION, the single highest-impact task */}
      <TodaysMissionCard
        mission={data.today_mission}
        calculating={data.readiness_calculating}
        interviewDate={data.interview_context?.interview_date}
      />

      {/* 🏆 MOMENTUM REEL, horizontal scroll of past career milestones.
          Self-hides when the user has no milestones yet, so new accounts
          don't see an empty rail. */}
      <MilestoneFeed />

      {/* Section: MISSION STATUS */}
      <section data-testid="section-mission-status">
        <SectionHeader
          label="Progress"
          subtitle="Know where you stand before your next interview."
          testid="mission-status-header"
        />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <InterviewContextCard ctx={data.interview_context} onUpdate={() => load()} />
          <InterviewReadinessCard
            score={data.readiness_score}
            band={data.readiness_band}
            trend={data.readiness_trend}
            projectedDelta={data.today_mission.projected_delta}
            coachMessage={data.coach_message}
            competencies={data.competencies}
            calculating={data.readiness_calculating}
            calculatingMessage={data.readiness_calculating_message}
            official={data.official_readiness}
            readinessSources={data.readiness_sources}
          />
          <FastestPathCard fastest={data.fastest_path} calculating={data.readiness_calculating} />
        </div>
      </section>

      {/* Locked Replay Previews, shown only to Free users */}
      {!data.premium && (
        <section data-testid="section-locked-replays">
          <SectionHeader
            label="Interview Replays"
            subtitle="Rewatch every mock with AI coaching notes overlaid."
            testid="locked-replays-header"
          />
          <LockedReplaysCard />
        </section>
      )}

      {/* Section: INTERVIEW READINESS FORECAST */}
      {data.readiness_forecast && (
        <section data-testid="section-readiness-forecast">
          <SectionHeader
            label="Interview Readiness Forecast"
            subtitle="Where your readiness lands if you finish the remaining steps."
            testid="readiness-forecast-header"
          />
          <ReadinessForecastCard forecast={data.readiness_forecast} />
        </section>
      )}

      {/* Section: YOUR SNAPSHOT */}
      <section data-testid="section-your-snapshot">
        <SectionHeader
          label="Your Snapshot"
          subtitle="Monitor your preparation at a glance."
          testid="your-snapshot-header"
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <MetricTile
            testid="metric-resume-alignment"
            label="Job Match"
            value={
              cm.resume_alignment.value !== null && cm.resume_alignment.value !== undefined ? (
                <>
                  <AnimatedNumber value={cm.resume_alignment.value} />
                  <span className="text-sm text-zinc-500 font-medium">%</span>
                </>
              ) : null
            }
            tone={cm.resume_alignment.value >= 80 ? "strong" : "default"}
          />
          <MetricTile
            testid="metric-ai-practice-sessions"
            label="AI Practice Sessions"
            value={<AnimatedNumber value={cm.ai_practice_sessions?.value || 0} />}
            subValue="Practice · Official AI"
          />
          <LatestBriefCard brief={data.latest_brief} />
        </div>
      </section>

      {/* Recent activity */}
      <div
        data-testid="card-recent"
        className="p-5 rounded-xl border border-zinc-800/70 bg-zinc-900/40"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-zinc-500">
            <Clock className="h-3 w-3" /> Recent Progress
          </div>
          <Link
            to="/app/history"
            data-testid="view-full-progress"
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-500 hover:text-amber-400 transition-colors"
          >
            <TrendingUp className="h-3 w-3" /> View Full Progress <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
        {data.recent_activity.length === 0 ? (
          <div className="text-sm text-zinc-500 py-6 text-center">
            Your journey starts with Stand Out. Analyze your fit above to take the first step.
          </div>
        ) : (
          <ul className="divide-y divide-zinc-800/60">
            {data.recent_activity.map((e, i) => (
              <li key={i} className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-8 w-8 rounded-md bg-zinc-800/70 border border-zinc-800 flex items-center justify-center">
                    {e.type === "mock" ? (
                      <Target className="h-3.5 w-3.5 text-amber-400" />
                    ) : (
                      <Building2 className="h-3.5 w-3.5 text-zinc-400" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm text-zinc-100 truncate">{e.title}</div>
                    <div className="text-[11px] text-zinc-500">{timeAgo(e.created_at)}</div>
                  </div>
                </div>
                {typeof e.score === "number" && (
                  <div
                    className={`text-xs font-semibold tabular-nums px-2.5 py-1 rounded-md border ${
                      e.score >= 80
                        ? "text-emerald-400 border-emerald-500/25 bg-emerald-500/5"
                        : e.score >= 55
                        ? "text-amber-400 border-amber-500/25 bg-amber-500/5"
                        : "text-violet-300 border-violet-500/25 bg-violet-500/5"
                    }`}
                  >
                    {e.score}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      </div>
    </div>
  );
}
